import { FC, CSSProperties, useMemo, useCallback } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { theme } from "antd";
import { valueToNumber } from "pages/[orgId]/explore/lib/common";
import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { Field } from "src/search/types";
import { SearchContext } from "src/search/useSearchContext";
import { asKey } from "src/utils/fields";
import { usePopupContext } from "src/hooks/usePopup";
import { mono } from "src/fonts";
import { Flex } from "./Flex";
import styles from "./hexagrid.module.css";
import { useMinMax } from "src/hooks/useMinMax";
import { useColorScheme } from "src/hooks/useColorScheme";
import {
  RampTheme,
  themeColors,
  hexToRgb,
  hslToHex,
  rgbToHsl,
  getContrastTextColor,
} from "src/utils/color";
import { HexagridCellTooltip } from "pages/[orgId]/explore/lib/tooltips/HexagridCellTooltip";

export const Hexagrid: FC<{
  data: {
    values: { value: string; label: string }[];
    value: { value: string; label: string };
    label: string;
  }[];
  context: SearchContext;
  invertComparisonColors: boolean;
  showValue?: boolean;
  hueReverse: boolean;
  hueShift: number;
  isMuted: boolean;
  isPastel: boolean;
  legend?: boolean;
  properties: Field[];
  onDrillDown?: () => void;
  size: number;
  theme: RampTheme;
}> = ({
  data,
  legend,
  showValue,
  properties,
  onDrillDown,
  context,
  hueReverse,
  hueShift,
  isPastel,
  isMuted,
  size,
  theme: colorTheme,
  invertComparisonColors,
}) => {
  const { token } = theme.useToken();
  const columnInfo = context.aggregations.data?.meta?.columnInfo || [];
  const aggField =
    context.params.state.aggregations?.at(0) ||
    columnInfo.find((c) => c.reducer);

  const values = useMemo(
    () => data.map(({ value }) => valueToNumber(value.value)),
    [data]
  );
  const [min, max] = useMinMax(values);
  const popup = usePopupContext();
  const hasCompareWith = context.params.state.compareWith?.method === "tuple";
  const mode = useColorScheme();

  const colors = useCallback(
    (value?: string | number) => {
      if (value === undefined || value === "__VZBL::null__")
        return token.colorBgContainer + "22";
      const numValue = typeof value === "string" ? Number(value) : value;

      // Calculate normalized value between 0 and 1
      const normalizedValue = (numValue - min) / (max - min);

      // Get theme colors array
      const themeColorArray = themeColors(
        colorTheme,
        mode,
        false, // isMonotone
        isMuted,
        isPastel,
        hasCompareWith
          ? invertComparisonColors
            ? !hueReverse
            : hueReverse
          : hueReverse,
        hueShift,
        12
      );

      // Select color based on normalized value
      const idx = Math.min(Math.floor(normalizedValue * 11), 11);
      return themeColorArray[idx];
    },
    [
      min,
      max,
      colorTheme,
      mode,
      isMuted,
      isPastel,
      hueReverse,
      hueShift,
      hasCompareWith,
      invertComparisonColors,
      token.colorBgContainer,
    ]
  );

  const total = values.reduce((acc, val) => acc + Number(val), 0);

  if (values.length === 0)
    return (
      <Flex centered style={{ height: "100%" }}>
        <LoadingOutlined />
      </Flex>
    );
  return (
    <div style={{ padding: 16 }}>
      {legend && (
        <div
          style={{
            height: 20,
            marginTop: 2,
            overflow: "auto",
            borderRadius: 2,
            padding: 2,
            clear: "both",
          }}
        >
          <Flex
            spaceBetween
            gap={4}
            style={{
              borderRadius: 2,
              fontSize: 12,
              padding: "1px 4px",
              color: token.colorTextLabel,
            }}
          >
            <span>{Number(min).toLocaleString()}</span>
            <div
              style={{
                flex: 1,
                height: 5,
                backgroundImage: `linear-gradient(to right, ${colors(
                  min
                )} 0%, ${
                  min < 0 && 0 < max
                    ? colors(-0.001) + " " + zeroPercent(min, max) + "%,"
                    : ""
                } ${
                  min < 0 && 0 < max
                    ? colors(0.001) + " " + zeroPercent(min, max) + "%,"
                    : ""
                } ${colors(max)} 100%)`,
              }}
            />
            <span>{Number(max).toLocaleString()}</span>
          </Flex>
        </div>
      )}
      <div
        className={styles.main}
        style={
          {
            ["--s"]: `${size}px`,
          } as CSSProperties
        }
      >
        <div className={styles.container}>
          {data.map(({ value, label, values }) => (
            <HexagridCellTooltip
              key={`${value.label}-${label}`}
              cell={{ value, label, values }}
              context={context}
              min={min}
              max={max}
              total={total}
            >
              <div
                className="saturate-hover"
                title={`${label} - ${value.label}`}
                style={{
                  backgroundColor: colors(value.value),
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  popup.setPopup(
                    e,
                    <FilterMenu
                      accentColor={colors(value.value)}
                      measureValue={{
                        label: asKey(aggField!),
                        value: value.value,
                      }}
                      {...{
                        context,
                        onDrillDown,
                        properties,
                        values,
                      }}
                    />
                  );
                }}
              >
                {showValue && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                      padding: 2,
                    }}
                  >
                    <div
                      className={mono.className}
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        height: "1em",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        clipPath: "initial",
                        margin: 0,
                        color: showValue
                          ? getContrastTextColor(colors(value.value))
                          : "transparent",
                      }}
                    >
                      {value.label}
                    </div>
                  </div>
                )}
              </div>
            </HexagridCellTooltip>
          ))}
        </div>
      </div>
    </div>
  );
};

function zeroPercent(min: number, max: number): number {
  if (min > 0) {
    return 0;
  } else if (max < 0) {
    return 100;
  } else {
    return Math.round(((0 - min) / (max - min)) * 100);
  }
}
